import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Package, Clock, Truck, CheckCircle2, XCircle, ChevronDown, ChevronUp, Phone, Mail, MapPin } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CONFIG = {
  pending: { label: 'In Attesa', icon: Clock, color: '#D98324', bg: 'bg-[#D98324]/10 text-[#D98324]' },
  confirmed: { label: 'Confermato', icon: CheckCircle2, color: '#4A6B53', bg: 'bg-[#4A6B53]/10 text-[#4A6B53]' },
  shipped: { label: 'Spedito', icon: Truck, color: '#0088cc', bg: 'bg-[#0088cc]/10 text-[#0088cc]' },
  delivered: { label: 'Consegnato', icon: CheckCircle2, color: '#2D5A3D', bg: 'bg-[#2D5A3D]/10 text-[#2D5A3D]' },
  cancelled: { label: 'Annullato', icon: XCircle, color: '#DC2626', bg: 'bg-red-100 text-red-600' },
};

export default function OrdersPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [expandedOrder, setExpandedOrder] = useState(null);

  const getHeaders = () => {
    const headers = {};
    if (user?.role === 'superadmin') {
      const activeShop = localStorage.getItem('phv_active_shop');
      if (activeShop) headers['X-Shop-Id'] = activeShop;
    }
    return headers;
  };

  useEffect(() => {
    if (!user || !['admin', 'superadmin'].includes(user.role)) {
      navigate('/account');
      return;
    }
    fetchOrders();
  }, [user, navigate, filterStatus]);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterStatus !== 'all') params.status = filterStatus;
      const { data } = await axios.get(`${API}/orders`, { withCredentials: true, headers: getHeaders(), params });
      setOrders(data.orders || []);
      setStats(data.stats || {});
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (orderId, newStatus) => {
    try {
      await axios.put(`${API}/orders/${orderId}/status`, { status: newStatus }, { withCredentials: true, headers: getHeaders() });
      toast.success(`Ordine aggiornato: ${STATUS_CONFIG[newStatus]?.label}`);
      fetchOrders();
    } catch (e) {
      toast.error('Errore aggiornamento stato');
    }
  };

  const totalOrders = Object.values(stats).reduce((a, b) => a + b, 0);

  return (
    <div data-testid="orders-page" className="pb-safe px-4 pt-4">
      <h1 className="text-2xl font-semibold text-[#2A2624] mb-4" style={{ fontFamily: 'Outfit' }}>
        Gestione Ordini
      </h1>

      {/* Stats Bar */}
      <div className="flex gap-2 overflow-x-auto no-scrollbar mb-4 pb-1">
        <button
          data-testid="filter-all"
          onClick={() => setFilterStatus('all')}
          className={`whitespace-nowrap px-3 py-2 rounded-xl text-xs font-bold transition-all ${
            filterStatus === 'all' ? 'bg-[#2A2624] text-white' : 'bg-[#F0EBE1] text-[#5C554F]'
          }`}
        >
          Tutti ({totalOrders})
        </button>
        {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
          <button
            key={key}
            data-testid={`filter-${key}`}
            onClick={() => setFilterStatus(key)}
            className={`whitespace-nowrap px-3 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-1 ${
              filterStatus === key ? 'bg-[#2A2624] text-white' : 'bg-[#F0EBE1] text-[#5C554F]'
            }`}
          >
            {cfg.label} ({stats[key] || 0})
          </button>
        ))}
      </div>

      {/* Orders List */}
      {loading ? (
        <div className="space-y-3">
          {[1,2,3].map(i => <div key={i} className="h-28 skeleton-shimmer rounded-xl" />)}
        </div>
      ) : orders.length === 0 ? (
        <div className="text-center py-16">
          <Package size={40} className="mx-auto text-[#877E78] mb-3" />
          <h3 className="text-lg font-medium text-[#2A2624]" style={{ fontFamily: 'Outfit' }}>
            Nessun ordine
          </h3>
          <p className="text-sm text-[#877E78] mt-1">
            {filterStatus === 'all' ? 'Non ci sono ancora ordini' : `Nessun ordine ${STATUS_CONFIG[filterStatus]?.label.toLowerCase()}`}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {orders.map(order => {
            const cfg = STATUS_CONFIG[order.status] || STATUS_CONFIG.pending;
            const StatusIcon = cfg.icon;
            const isExpanded = expandedOrder === order.id;

            return (
              <div
                key={order.id}
                data-testid={`order-card-${order.id}`}
                className="bg-white rounded-xl border border-[#E8E3D9] overflow-hidden"
              >
                {/* Header */}
                <div
                  className="p-4 cursor-pointer"
                  onClick={() => setExpandedOrder(isExpanded ? null : order.id)}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${cfg.bg}`}>
                          <StatusIcon size={10} className="inline mr-0.5" />
                          {cfg.label}
                        </span>
                        <span className="text-[10px] text-[#877E78]">
                          #{order.id.slice(0, 8)}
                        </span>
                      </div>
                      <h4 className="font-medium text-sm text-[#2A2624]">{order.customer_name}</h4>
                      <p className="text-xs text-[#877E78]">
                        {new Date(order.created_at).toLocaleDateString('it-IT', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                    <div className="text-right flex items-center gap-2">
                      <span className="text-lg font-bold text-[#C25934]" style={{ fontFamily: 'Outfit' }}>
                        {order.total?.toFixed(2)} &euro;
                      </span>
                      {isExpanded ? <ChevronUp size={16} className="text-[#877E78]" /> : <ChevronDown size={16} className="text-[#877E78]" />}
                    </div>
                  </div>
                  <p className="text-xs text-[#877E78] mt-1">
                    {order.items?.length || 0} articol{(order.items?.length || 0) === 1 ? 'o' : 'i'}
                  </p>
                </div>

                {/* Expanded Detail */}
                {isExpanded && (
                  <div className="border-t border-[#E8E3D9] p-4 space-y-4">
                    {/* Customer Info */}
                    <div className="space-y-1.5">
                      <h5 className="text-xs font-bold text-[#2A2624] uppercase tracking-wider">Cliente</h5>
                      <div className="flex items-center gap-2 text-sm text-[#5C554F]">
                        <Phone size={13} /> {order.customer_phone}
                      </div>
                      <div className="flex items-center gap-2 text-sm text-[#5C554F]">
                        <Mail size={13} /> {order.customer_email}
                      </div>
                      <div className="flex items-center gap-2 text-sm text-[#5C554F]">
                        <MapPin size={13} /> {order.customer_address}
                      </div>
                      {order.notes && (
                        <p className="text-xs text-[#877E78] bg-[#F0EBE1] rounded-lg p-2 mt-1">
                          Note: {order.notes}
                        </p>
                      )}
                    </div>

                    {/* Items */}
                    <div>
                      <h5 className="text-xs font-bold text-[#2A2624] uppercase tracking-wider mb-2">Articoli</h5>
                      {order.items?.map((item, idx) => (
                        <div key={idx} className="flex justify-between items-center py-1.5 border-b border-[#E8E3D9] last:border-0">
                          <div>
                            <span className="text-sm text-[#2A2624]">{item.product_title}</span>
                            {item.selected_variants && Object.keys(item.selected_variants).length > 0 && (
                              <span className="text-[10px] text-[#877E78] ml-1">
                                ({Object.entries(item.selected_variants).map(([k, v]) => `${k}: ${v}`).join(', ')})
                              </span>
                            )}
                          </div>
                          <span className="text-sm font-medium text-[#5C554F]">
                            {item.quantity}x {item.product_price?.toFixed(2)} &euro;
                          </span>
                        </div>
                      ))}
                    </div>

                    {/* Update Status */}
                    <div>
                      <h5 className="text-xs font-bold text-[#2A2624] uppercase tracking-wider mb-2">Aggiorna Stato</h5>
                      <div className="flex flex-wrap gap-2">
                        {Object.entries(STATUS_CONFIG).map(([key, scfg]) => {
                          const SIcon = scfg.icon;
                          return (
                            <button
                              key={key}
                              data-testid={`set-status-${key}-${order.id}`}
                              onClick={() => updateStatus(order.id, key)}
                              disabled={order.status === key}
                              className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                                order.status === key
                                  ? 'bg-[#2A2624] text-white'
                                  : 'bg-[#F0EBE1] text-[#5C554F] hover:bg-[#E6E2D6]'
                              }`}
                            >
                              <SIcon size={12} /> {scfg.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
